import { Stack } from "@mui/material";
import { type FC } from "react";

import { CoreChart } from "~/feature/chart/components/CoreChart";
import { CoreChartProvider } from "~/feature/chart/providers/CoreChartProvider";
import { Breadcrumbs } from "~/feature/event/components/Breadcrumbs";
import { EventHeader } from "~/feature/event/components/EventHeader";
import { FaqSection } from "~/feature/event/components/FAQSection";
import {
  MarketCalculator,
  MarketCalculatorLine,
} from "~/feature/event/components/MarketCalculator";
import { MarketsTabs } from "~/feature/event/components/MarketsTabs";
import { OfficialPlatformRules } from "~/feature/event/components/OfficialPlatformRules";
import { EventPageSections } from "~/feature/event/constants";
import { NewsSection } from "~/feature/news/components/NewsSection";
import { RelatedEventsSection } from "~/feature/relatedEvents/components/RelatedEventsSection";
import type { ICategory } from "~/types/category";
import type {
  IAggregatedEvent,
  IMarket,
  IOutcomeConsensus,
} from "~/types/event";

import { AlertSection } from "../AlertSection";
import { Intro } from "../Intro";
import * as Styled from "./EventContent.styles";
import { useCalculatorVisibility } from "./useCalculatorVisibility";

interface IActiveEventContentProps {
  event: IAggregatedEvent | null;
  isGroupedEvents: boolean;
  category: ICategory | null;
  markets: IMarket[];
  outcomes: IOutcomeConsensus[];
}

export const EventContent: FC<IActiveEventContentProps> = ({
  event,
  isGroupedEvents,
  category,
  markets,
  outcomes,
}) => {
  const { calculatorRef, marketsTableRef } = useCalculatorVisibility();

  if (!event) {
    return null;
  }

  return (
    <CoreChartProvider event={event} markets={markets} outcomes={outcomes}>
      <Styled.Root>
        <Stack sx={{
          gap: 3
        }}>
          <Breadcrumbs title={event.title} category={category} />
          <Stack
            direction="row"
            sx={{
              alignItems: "flex-start",
              gap: 2
            }}>
            <Styled.LeftContainer>
              <Styled.ContentStack>
                <EventHeader event={event} />
                <Styled.ChartSection
                  component="section"
                  id={EventPageSections.EventChart}
                >
                  <CoreChart markets={markets} />
                </Styled.ChartSection>
                <NewsSection eventId={event.id} />
              </Styled.ContentStack>
            </Styled.LeftContainer>
            <Styled.RightContainer ref={calculatorRef}>
              <MarketCalculator event={event} />
            </Styled.RightContainer>
          </Stack>
        </Stack>
        <Styled.SectionsStack>
          <MarketsTabs event={event} ref={marketsTableRef} />
          <Styled.EventSection>
            <Intro
              id={
                event.seo?.intro
                  ? EventPageSections.Intro
                  : EventPageSections.MarketDescription
              }
              content={event.seo?.intro ? event.seo?.intro : event.description}
              title={event.seo?.intro ? "Intro" : "Description"}
            />
          </Styled.EventSection>
          {isGroupedEvents && (
            <Styled.EventSection>
              <AlertSection
                resolutionRules={event.resolutionRulesDetails}
                matchConfidence={event.matchConfidence ?? undefined}
              />
            </Styled.EventSection>
          )}
          <Styled.EventSection>
            <OfficialPlatformRules event={event} />
          </Styled.EventSection>
          <Styled.EventSection>
            <FaqSection faqs={event.seo.faqs || []} />
          </Styled.EventSection>
          <Styled.EventSection>
            <RelatedEventsSection relatedEvents={event.relatedEvents || []} />
          </Styled.EventSection>
        </Styled.SectionsStack>
      </Styled.Root>
      <MarketCalculatorLine event={event} />
    </CoreChartProvider>
  );
};
